import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { MessageSquare, Clock, DollarSign, User, MapPin, Calendar, Phone, Mail, Send, CheckCircle, XCircle, AlertCircle, BookOpen } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from "@/i18n/I18nProvider";
import { formatCurrency } from "@/lib/currency";
import QuoteToBookingConverter from "./QuoteToBookingConverter";

const quoteResponseSchema = z.object({
  price: z.number().min(0.01, "Price must be greater than 0"),
  message: z.string().min(10, "Message must be at least 10 characters"),
  estimated_duration: z.number().min(15, "Duration must be at least 15 minutes"),
  available_dates: z.array(z.string()).optional().default([]),
});

type QuoteResponseForm = z.infer<typeof quoteResponseSchema>;

interface QuoteRequest {
  id: string;
  customer_id: string;
  company_id: string;
  service_id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  service_name: string;
  square_meters: number;
  photos: string[];
  special_requests: string;
  status: 'pending' | 'quoted' | 'accepted' | 'rejected' | 'expired' | 'converted';
  estimated_price_min: number;
  estimated_price_max: number;
  quote_price: number;
  quote_message: string;
  scheduled_date?: string;
  scheduled_time?: string;
  service_address_id?: string;
  created_at: string;
  updated_at: string;
  address: string;
}

interface QuoteRequestManagerProps {
  companyId: string;
  onQuoteSent: (requestId: string, quote: QuoteResponseForm) => void;
  onStatusUpdate: (requestId: string, status: string) => void;
}

export default function QuoteRequestManager({ companyId, onQuoteSent, onStatusUpdate }: QuoteRequestManagerProps) {
  const [quoteRequests, setQuoteRequests] = useState<QuoteRequest[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<QuoteRequest | null>(null);
  const [isQuoteDialogOpen, setIsQuoteDialogOpen] = useState(false);
  const [isBookingConverterOpen, setIsBookingConverterOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'pending' | 'quoted' | 'accepted' | 'converted'>('all');
  const { toast } = useToast();
  const { t, tx } = useI18n();

  const form = useForm<QuoteResponseForm>({
    resolver: zodResolver(quoteResponseSchema),
    defaultValues: {
      price: 0,
      message: "",
      estimated_duration: 60,
      available_dates: [],
    },
  });

  // Load from Supabase
  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        // Resolve company id from prop
        const { data: company } = await (supabase as any).from('companies').select('id').eq('id', companyId).maybeSingle();
        const cid = company?.id || companyId;
        const { data, error } = await (supabase as any)
          .from('quote_requests')
          .select('id, customer_id, company_id, service_id, square_meters, photos, special_requests, status, estimated_price_min, estimated_price_max, quote_price, quote_message, scheduled_date, scheduled_time, customer_name, customer_phone, customer_email, service_address_id, created_at, updated_at')
          .eq('company_id', cid)
          .order('created_at', { ascending: false });
        if (error) throw error;
        // Enrich with basic joins if needed later
        const mapped: QuoteRequest[] = (data || []).map((r: any) => ({
          id: r.id,
          customer_id: r.customer_id,
          company_id: r.company_id,
          service_id: r.service_id,
          customer_name: r.customer_name || r.customer_id || 'Customer',
          customer_email: r.customer_email || '',
          customer_phone: r.customer_phone || '',
          service_name: String(r.service_id || 'Service'),
          square_meters: r.square_meters || 0,
          photos: r.photos || [],
          special_requests: r.special_requests || '',
          status: r.status,
          estimated_price_min: r.estimated_price_min || 0,
          estimated_price_max: r.estimated_price_max || 0,
          quote_price: r.quote_price || 0,
          quote_message: r.quote_message || '',
          scheduled_date: r.scheduled_date,
          scheduled_time: r.scheduled_time,
          service_address_id: r.service_address_id,
          created_at: r.created_at,
          updated_at: r.updated_at,
          address: ''
        }));
        setQuoteRequests(mapped);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [companyId]);

  const filteredRequests = quoteRequests.filter(request => {
    if (filter === 'all') return true;
    return request.status === filter;
  });

  const handleSendQuote = async (data: QuoteResponseForm) => {
    if (!selectedRequest) return;
    try {
      const { error } = await (supabase as any)
        .from('quote_requests')
        .update({ status: 'quoted', quote_price: data.price, quote_message: data.message, updated_at: new Date().toISOString() })
        .eq('id', selectedRequest.id);
      if (error) throw error;
      setQuoteRequests(prev => prev.map(req => req.id === selectedRequest.id ? { ...req, status: 'quoted', quote_price: data.price, quote_message: data.message, updated_at: new Date().toISOString() } : req));
      onQuoteSent(selectedRequest.id, data);
      setIsQuoteDialogOpen(false);
      form.reset();
      toast({ title: t('vendor.quotes.toast.sent_title'), description: t('vendor.quotes.toast.sent_desc').replace('{{price}}', String(data.price)) });
    } catch (e: any) {
      toast({ title: t('common.error'), description: e.message || 'Failed to send quote', variant: 'destructive' });
    }
  };

  const handleStatusUpdate = async (requestId: string, newStatus: string) => {
    try {
      const { error } = await (supabase as any).from('quote_requests').update({ status: newStatus }).eq('id', requestId);
      if (error) throw error;
      setQuoteRequests(prev => prev.map(req => req.id === requestId ? { ...req, status: newStatus as any } : req));
      onStatusUpdate(requestId, newStatus);
      toast({ title: t('vendor.quotes.toast.status_updated'), description: tx('vendor.quotes.toast.status_changed', { status: newStatus }) });
    } catch (e: any) {
      toast({ title: t('common.error'), description: e.message || 'Failed to update status', variant: 'destructive' });
    }
  };

  const handleConvertToBooking = (request: QuoteRequest) => {
    setSelectedRequest(request);
    setIsBookingConverterOpen(true);
  };

  const handleBookingCreated = (bookingId: string) => {
    toast({
      title: "Booking Created",
      description: "The quote has been successfully converted to a booking.",
    });
    setIsBookingConverterOpen(false);
    setSelectedRequest(null);
    loadQuoteRequests();
    onStatusUpdate(selectedRequest?.id || '', 'converted');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500';
      case 'quoted': return 'bg-blue-500';
      case 'accepted': return 'bg-green-500';
      case 'rejected': return 'bg-red-500';
      case 'expired': return 'bg-gray-500';
      case 'converted': return 'bg-purple-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return Clock;
      case 'quoted': return MessageSquare;
      case 'accepted': return CheckCircle;
      case 'rejected': return XCircle;
      case 'expired': return AlertCircle;
      default: return Clock;
    }
  };

  return (
    <div className="space-y-6 w-full overflow-hidden">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 break-words">
            <MessageSquare className="h-5 w-5" />
            {t('vendor.quotes.title')}
          </CardTitle>
          <CardDescription className="break-words">
            {t('vendor.quotes.desc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="max-w-full overflow-x-auto">
          <Tabs value={filter} onValueChange={(value) => setFilter(value as any)} className="w-full min-w-0">
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4">
              <TabsTrigger value="all">{t('vendor.quotes.tabs.all')} ({quoteRequests.length})</TabsTrigger>
              <TabsTrigger value="pending">{t('vendor.quotes.tabs.pending')} ({quoteRequests.filter(r => r.status === 'pending').length})</TabsTrigger>
              <TabsTrigger value="quoted">{t('vendor.quotes.tabs.quoted')} ({quoteRequests.filter(r => r.status === 'quoted').length})</TabsTrigger>
              <TabsTrigger value="accepted">{t('vendor.quotes.tabs.accepted')} ({quoteRequests.filter(r => r.status === 'accepted').length})</TabsTrigger>
            </TabsList>

            <div className="mt-6 space-y-4">
              {filteredRequests.map((request) => {
                const StatusIcon = getStatusIcon(request.status);
                return (
                  <Card key={request.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <Avatar>
                              <AvatarFallback>
                                {request.customer_name.split(' ').map(n => n[0]).join('')}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <h3 className="font-semibold">{request.customer_name}</h3>
                              <p className="text-sm text-muted-foreground">{request.service_name}</p>
                            </div>
                            <Badge className={getStatusColor(request.status)}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                            </Badge>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div className="flex items-center gap-2 text-sm">
                              <MapPin className="h-4 w-4 text-muted-foreground" />
                              <span>{request.address}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <DollarSign className="h-4 w-4 text-muted-foreground" />
                              <span>{request.square_meters} m²</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              <span>{format(new Date(request.created_at), 'MMM d, yyyy')}</span>
                            </div>
                          </div>

                          {request.special_requests && (
                            <div className="mb-4">
                              <h4 className="font-medium mb-1">{t('vendor.quotes.special_requests')}</h4>
                              <p className="text-sm text-muted-foreground">{request.special_requests}</p>
                            </div>
                          )}

                          {request.status === 'quoted' && (
                            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                              <h4 className="font-medium mb-1">{t('vendor.quotes.your_quote')}</h4>
                              <p className="text-sm text-muted-foreground mb-2">{request.quote_message}</p>
                              <p className="font-semibold text-blue-600">{formatCurrency(request.quote_price)}</p>
                            </div>
                          )}

                          {request.photos.length > 0 && (
                            <div className="mb-4">
                              <h4 className="font-medium mb-2">{t('vendor.quotes.photos').replace('{{count}}', String(request.photos.length))}</h4>
                              <div className="flex gap-2">
                                {request.photos.map((photo, index) => (
                                  <div key={index} className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                                    <span className="text-xs text-gray-500">{t('vendor.quotes.photo_label').replace('{{index}}', String(index + 1))}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col gap-2 ml-4">
                          {request.status === 'pending' && (
                            <Dialog open={isQuoteDialogOpen} onOpenChange={setIsQuoteDialogOpen}>
                              <DialogTrigger asChild>
                                <Button 
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    form.setValue('price', request.estimated_price_min);
                                    setIsQuoteDialogOpen(true);
                                  }}
                                  size="sm"
                                >
                                  <Send className="h-4 w-4 mr-1" />
                                  {t('vendor.quotes.send')}
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-md">
                                <DialogHeader>
                                  <DialogTitle>{t('vendor.quotes.send_to').replace('{{name}}', request.customer_name)}</DialogTitle>
                                  <DialogDescription>
                                    {t('vendor.quotes.send_desc')}
                                  </DialogDescription>
                                </DialogHeader>
                                <Form {...form}>
                                  <form onSubmit={form.handleSubmit(handleSendQuote)} className="space-y-4" onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>
                                    <FormField
                                      control={form.control}
                                      name="price"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>{t('vendor.quotes.form.price')}</FormLabel>
                                          <FormControl>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              {...field}
                                              onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />

                                    <FormField
                                      control={form.control}
                                      name="estimated_duration"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>{t('vendor.quotes.form.duration')}</FormLabel>
                                          <FormControl>
                                            <Input
                                              type="number"
                                              {...field}
                                              onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />

                                    <FormField
                                      control={form.control}
                                      name="message"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>{t('vendor.quotes.form.message')}</FormLabel>
                                          <FormControl>
                                            <Textarea
                                              placeholder={t('vendor.quotes.form.message_placeholder')}
                                              {...field}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />

                                    <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
                                      {t('vendor.quotes.send')}
                                    </Button>
                                  </form>
                                </Form>
                              </DialogContent>
                            </Dialog>
                          )}

                          {request.status === 'quoted' && (
                            <div className="flex flex-col gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleStatusUpdate(request.id, 'accepted')}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                {t('vendor.quotes.btn.accept')}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleStatusUpdate(request.id, 'rejected')}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                {t('vendor.quotes.btn.reject')}
                              </Button>
                            </div>
                          )}

                          {request.status === 'accepted' && (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleConvertToBooking(request)}
                              className="bg-purple-600 hover:bg-purple-700"
                            >
                              <BookOpen className="h-4 w-4 mr-1" />
                              Create Booking
                            </Button>
                          )}

                          {request.status === 'converted' && (
                            <div className="flex items-center gap-2 text-sm text-purple-600">
                              <BookOpen className="h-4 w-4" />
                              <span>Converted to Booking</span>
                            </div>
                          )}

                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Phone className="h-4 w-4" />
                            <span>{request.customer_phone}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Mail className="h-4 w-4" />
                            <span>{request.customer_email}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {filteredRequests.length === 0 && (
                <div className="text-center py-8">
                  <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{t('vendor.quotes.no_requests_title')}</h3>
                  <p className="text-muted-foreground">
                    {filter === 'all' 
                      ? t('vendor.quotes.no_requests_all')
                      : t('vendor.quotes.no_requests_filter').replace('{{status}}', filter)
                    }
                  </p>
                </div>
              )}
            </div>
          </Tabs>
        </CardContent>
      </Card>

      {/* Quote to Booking Converter Dialog */}
      {isBookingConverterOpen && selectedRequest && (
        <QuoteToBookingConverter
          quoteRequest={selectedRequest}
          onBookingCreated={handleBookingCreated}
          onClose={() => {
            setIsBookingConverterOpen(false);
            setSelectedRequest(null);
          }}
        />
      )}
    </div>
  );
} 